'use client';

import { User } from 'common/interfaces/api';
import { isHavingPrimeBenefits } from 'common/utils/payment';
import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import {
  createForumComment,
  deleteForumComment,
  ForumCommentType,
  ForumRepliesResponseType,
  getForumReplies,
} from 'service/forum';
import useGlobalStore from 'store/global';

import { Button } from 'components/v2/atoms/button';
import useStore from 'store/timeline';
import ForumCommentBox from './forum-comment-box';
import ForumCommentItem from './forum-comment-item';
import ForumReplyList from './forum-reply-list';

interface ForumCommentDetailPropsType {
  commentId: number;
  creator: User;
  onBack: () => void;
}

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex cursor-pointer items-center gap-2 border-none bg-transparent text-sm text-pp-text-primary transition-colors hover:underline"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15,18 9,12 15,6"></polyline>
    </svg>
    掲示板に戻る
  </button>
);

const ForumCommentDetail = ({
  commentId,
  creator,
  onBack,
}: ForumCommentDetailPropsType) => {
  const creatorId = creator.id;
  const queryClient = useQueryClient();
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const currentUser = useStore((state) => state.currentUser);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const currentAuth = useGlobalStore((state) => state.currentAuth);

  // Check if user has prime benefits for this creator
  const hasSubscription = isHavingPrimeBenefits(creator);
  const isCreatorMySelf = !!currentUser?.id && currentUser?.id === creatorId;
  const haveForumPermission = hasSubscription || isCreatorMySelf;

  const handleSubscribe = () => {
    if (!creator) return;
    if (creator.is_open_for_subscription === false) {
      setSnackbarMessage({
        type: 'error',
        text: 'このクリエイターは現在サブスクリプションを受け付けていません',
      });
      return;
    }
    showPaymentDialog({ creator });
  };

  // Get next page param for infinite query (cursor-based)
  const getNextPageParam = useCallback((lastPage: ForumRepliesResponseType) => {
    const replies = lastPage?.data?.replies;
    if (lastPage.has_next && replies && replies.length > 0) {
      const lastReply = replies[replies.length - 1];
      return lastReply.id;
    }
    return undefined;
  }, []);

  // Fetch replies (first request includes parent comment)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<ForumRepliesResponseType>(
    ['forum-replies', commentId?.toString()],
    async ({ pageParam }) => {
      return getForumReplies(commentId, pageParam as number | undefined);
    },
    {
      getNextPageParam,
      refetchOnWindowFocus: false,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      enabled:
        !!creatorId &&
        !!commentId &&
        isAuthInit &&
        !!currentAuth &&
        !!haveForumPermission,
    },
  );

  // Extract comment from first page
  const detailComment = data?.pages[0]?.data?.comment || null;

  // Extract all replies from all pages
  const replies: ForumCommentType[] = useMemo(
    () =>
      data?.pages
        ? data.pages.reduce<ForumCommentType[]>((curr, page) => {
            const items = page.data?.replies || [];
            return [...curr, ...items];
          }, [])
        : [],
    [data?.pages],
  );

  // Create reply mutation
  const { mutateAsync: createReply } = useMutation(
    async ({ parentId, text }: { parentId: number; text: string }) => {
      return createForumComment(creatorId, text, parentId);
    },
  );

  // Delete comment/reply mutation (detail view only)
  const { mutate: deleteComment } = useMutation(
    async (targetCommentId: number) => {
      await deleteForumComment(targetCommentId);
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(['forum-replies', commentId]);
        setSnackbarMessage({ type: 'success', text: 'コメントを削除しました' });
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: 'コメントの削除に失敗しました',
        });
      },
    },
  );

  const handleReplySubmit = async (text: string) => {
    await createReply({ parentId: commentId, text });
    void queryClient.invalidateQueries(['forum-replies', commentId]);
    setSnackbarMessage({ type: 'success', text: '返信を投稿しました' });
  };

  const handleDelete = (targetCommentId: number) => {
    deleteComment(targetCommentId);
    // If deleting the main comment, go back to list
    if (targetCommentId === commentId) {
      onBack();
    }
  };

  // Show subscription prompt when not subscribed
  if (!haveForumPermission) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} />
        <div className="relative rounded-xl bg-white p-6 text-center shadow-sm">
          <div className="mb-3 text-4xl">🎉</div>
          <h2 className="mb-3 text-lg font-bold text-pp-text-primary">
            プライム会員専用掲示板へようこそ！
          </h2>
          <p className="mx-auto mb-4 max-w-md text-sm leading-relaxed text-pp-text-secondary">
            ここはプライム登録者のみが参加できる、特別なコミュニティスペースです。
            日々のマーケットを深く読み解き、メンバー同士で知見を共有しましょう。
            <br />
            独自の視点やマーケットに関する分析など、あなたの積極的なコメントをお待ちしています！
          </p>
          <Button
            variant="defaultGreen"
            size="md"
            onClick={handleSubscribe}
            className="rounded-full px-6"
          >
            掲示板に参加する
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} />
        <div className="py-8 text-center">
          <div className="inline-block size-8 animate-spin rounded-full border-4 border-gray-200 border-t-pp-green-primary"></div>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (isError || !detailComment) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} />
        <div className="py-12 text-center">
          <div className="mb-2 text-4xl">🔍</div>
          <p className="text-pp-text-secondary">
            コメントが見つかりませんでした
          </p>
        </div>
      </div>
    );
  }

  // Detail view
  return (
    <div className="space-y-4">
      <BackButton onClick={onBack} />

      {/* Main comment */}
      <ForumCommentItem
        comment={detailComment}
        profileUserId={creatorId}
        profileUsername={creator.username}
        onDelete={handleDelete}
        isDetail
      />

      {/* Reply box */}
      <ForumCommentBox
        onSubmit={handleReplySubmit}
        placeholder="返信を入力..."
      />

      {/* Replies list */}
      <ForumReplyList
        replies={replies}
        profileUserId={creatorId}
        profileUsername={creator.username}
        onDelete={handleDelete}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage ?? false}
        isFetchingNextPage={isFetchingNextPage}
        isError={isError}
      />
    </div>
  );
};

export default ForumCommentDetail;
